import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Search, RefreshCcw, Printer, Building2 } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const months = [
  { value: "Select", label: "Select" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const SpecialHraReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO"; // must exist in your app

  const screenBadge = useMemo(() => "INFRA SCREEN : C-42", []);

  // meta
  const [branches, setBranches] = useState([{ fld_branchid: "Select", fld_branchname: "Select" }]);
  const [years, setYears] = useState([{ value: "Select", label: "Select" }]);
  const [metaLoading, setMetaLoading] = useState(false);

  // filters
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [branchId, setBranchId] = useState("Select");
  const [month, setMonth] = useState("Select");
  const [year, setYear] = useState("Select");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // autocomplete
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [showCodeSug, setShowCodeSug] = useState(false);
  const [showNameSug, setShowNameSug] = useState(false);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const loadMeta = async () => {
    if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");

    try {
      setMetaLoading(true);
      const res = await axios.get(`${BASE_URL}/api/special-hra-report/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId || 0),
          branchType: String(branchType || ""),
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setYears(res.data.years || []);
        setBranchId(String(res.data.defaultBranchId || "Select"));
        setYear(String(res.data.defaultYear || "Select"));
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setMetaLoading(false);
    }
  };

  const fetchList = async (p = 1) => {
    if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");
    if (!String(year).trim() || year === "Select") return toast.warn("Please select Year.");

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/special-hra-report/data`, {
        params: {
          finYearId: Number(finYearId),
          loginBranchId: Number(loginBranchId || 0),
          branchType: String(branchType || ""),
          branchId,
          month,
          year,
          fromDate,
          toDate,
          empCode: empCode.trim(),
          empName: empName.trim(),
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load report.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // --- autocomplete helpers (simple debounce)
  useEffect(() => {
    const t = setTimeout(async () => {
      const q = empCode.trim();
      if (!q) return setCodeSug([]);
      try {
        const res = await axios.get(`${BASE_URL}/api/special-hra-report/autocomplete/empcode`, {
          params: { finYearId: Number(finYearId), branchId, branchType, q },
        });
        if (res?.data?.success) setCodeSug(res.data.items || []);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empCode]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const q = empName.trim();
      if (!q) return setNameSug([]);
      try {
        const res = await axios.get(`${BASE_URL}/api/special-hra-report/autocomplete/empname`, {
          params: { finYearId: Number(finYearId), branchId, branchType, q },
        });
        if (res?.data?.success) setNameSug(res.data.items || []);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empName]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    setEmpCode("");
    setEmpName("");
    setFromDate("");
    setToDate("");
    setMonth("Select");
    setPage(1);
    await fetchList(1);
  };

  const handlePrint = () => {
    if (!String(year).trim() || year === "Select") {
      toast.warn("Please select Year.");
      return;
    }
    const url =
      `/PayRoll/SpecialHRAReport_Print?` +
      `finYearId=${encodeURIComponent(finYearId)}` +
      `&loginBranchId=${encodeURIComponent(loginBranchId)}` +
      `&branchType=${encodeURIComponent(branchType)}` +
      `&branchId=${encodeURIComponent(branchId)}` +
      `&month=${encodeURIComponent(month)}` +
      `&year=${encodeURIComponent(year)}` +
      `&fromDate=${encodeURIComponent(fromDate)}` +
      `&toDate=${encodeURIComponent(toDate)}` +
      `&empCode=${encodeURIComponent(empCode.trim())}` +
      `&empName=${encodeURIComponent(empName.trim())}`;

    window.open(url, "_blank", "width=900,height=900,scrollbars=yes,resizable=yes");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SPECIAL HRA REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Header row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Special HRA Listing
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter / Search / Pagination / Print
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading || metaLoading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrint} className={primaryBtn} disabled={loading || metaLoading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-5 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {/* Emp Code */}
              <div className="relative">
                <label className={labelBase}>Employee Code</label>
                <input
                  value={empCode}
                  onChange={(e) => setEmpCode(e.target.value)}
                  onFocus={() => setShowCodeSug(true)}
                  onBlur={() => setTimeout(() => setShowCodeSug(false), 150)}
                  className={inputBase + " mt-2"}
                  placeholder="Type emp code..."
                />
                {showCodeSug && codeSug.length > 0 && (
                  <div className={`absolute z-20 mt-1 w-full rounded-2xl border ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"} max-h-56 overflow-auto`}>
                    {codeSug.map((it, i) => (
                      <div
                        key={i}
                        className={`px-4 py-2 cursor-pointer text-sm ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
                        onMouseDown={() => setEmpCode(it)}
                      >
                        {it}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Emp Name */}
              <div className="relative">
                <label className={labelBase}>Employee Name</label>
                <input
                  value={empName}
                  onChange={(e) => setEmpName(e.target.value)}
                  onFocus={() => setShowNameSug(true)}
                  onBlur={() => setTimeout(() => setShowNameSug(false), 150)}
                  className={inputBase + " mt-2"}
                  placeholder="Type emp name..."
                />
                {showNameSug && nameSug.length > 0 && (
                  <div className={`absolute z-20 mt-1 w-full rounded-2xl border ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"} max-h-56 overflow-auto`}>
                    {nameSug.map((it, i) => (
                      <div
                        key={i}
                        className={`px-4 py-2 cursor-pointer text-sm ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
                        onMouseDown={() => setEmpName(it)}
                      >
                        {it}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className={inputBase + " mt-2"}
                  disabled={branchType !== "HO"}
                >
                  {branches.map((b, i) => (
                    <option key={i} value={b.fld_branchid}>
                      {b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>

              {/* Month */}
              <div>
                <label className={labelBase}>Month</label>
                <select value={month} onChange={(e) => setMonth(e.target.value)} className={inputBase + " mt-2"}>
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className={labelBase}>Year</label>
                <select value={year} onChange={(e) => setYear(e.target.value)} className={inputBase + " mt-2"}>
                  {years.map((y) => (
                    <option key={y.value} value={y.value}>
                      {y.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Dates */}
              <div>
                <label className={labelBase}>From Date (dd/MM/yyyy)</label>
                <input value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputBase + " mt-2"} placeholder="dd/MM/yyyy" />
              </div>

              <div>
                <label className={labelBase}>To Date (dd/MM/yyyy)</label>
                <input value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputBase + " mt-2"} placeholder="dd/MM/yyyy" />
              </div>
            </div>

            <div className="mt-5 flex justify-center">
              <button type="submit" className={primaryBtn} disabled={loading || metaLoading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center font-bold">S.No</th>
                    <th className="px-4 py-3 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-3 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-3 text-center font-bold">Branch</th>
                    <th className="px-4 py-3 text-center font-bold">Designation</th>
                    <th className="px-4 py-3 text-center font-bold">Special HRA</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 6 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employeecode}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employee}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_branchname}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_designationname}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center font-bold ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.fld_specialhra}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1 ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1 ? `${primaryBtnClass} text-white` : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default SpecialHraReport;
